// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

interface PasswordInputProps
  extends Omit<React.ComponentProps<'input'>, 'type'> {
  /**
   * What the field holds, spliced into the toggle's accessible name:
   * "Show password", "Hide vault passphrase". A screen reader announces the
   * button on its own, out of reach of the visible label, so a bare "Show"
   * would not say what is about to be revealed — and on the pages with two
   * of these it would not say which one.
   */
  reveals?: string;
  /** Classes for the positioning wrapper, for callers that size the field. */
  wrapperClassName?: string;
}

/**
 * A password field with an eye toggle inside the box.
 *
 * Typing a password blind is guesswork, and the cost of a typo is an error
 * message that cannot tell you which character you got wrong. Revealing is
 * opt-in and resets to hidden on every mount, so nothing is ever shown by
 * default; the toggle stays in the tab order so it can be reached without a
 * mouse.
 */
export function PasswordInput({
  className,
  wrapperClassName,
  reveals = 'password',
  disabled,
  ...props
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;

  return (
    <div className={cn('relative', wrapperClassName)}>
      <input
        {...props}
        type={visible ? 'text' : 'password'}
        disabled={disabled}
        // pr-10 last so it wins over the px-* the call sites pass.
        className={cn(className, 'pr-10')}
      />
      <button
        type="button"
        onClick={() => setVisible((shown) => !shown)}
        disabled={disabled}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
        aria-label={visible ? `Hide ${reveals}` : `Show ${reveals}`}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
